/**
 * useMilestones - 里程碑逻辑 Hook
 *
 * 负责计算里程碑列表、当前进度、下一个里程碑等。
 */

import { computed, type ComputedRef, type Ref } from 'vue-demi';
import type { MilestoneRewardItem } from '@ark/material-checkin-shared';
import { getMilestoneRewardValue } from '@ark/material-checkin-shared';
import type { DailyCheckinStats } from './useCheckinJournal';
import type { TaskSnapshot } from './usePageData';

/**
 * 里程碑项
 */
export interface Milestone {
  /** 里程碑天数 */
  days: number;
  /** 奖励金额 */
  reward: number;
  /** 奖励名称（多语言） */
  rewardName?: string;
  /** 是否已达成 */
  claimed: boolean;
}

/**
 * useMilestones Hook 配置
 */
export interface UseMilestonesOptions {
  /** 任务配置列表 */
  tasks: TaskSnapshot[] | Ref<TaskSnapshot[]>;
  /** 里程碑奖励配置 */
  milestoneRewards: MilestoneRewardItem[] | Ref<MilestoneRewardItem[]>;
  /** 每日打卡统计数据 */
  dailyCheckinStats: DailyCheckinStats | Ref<DailyCheckinStats>;
  /** 是否为英文环境 */
  isEnglish?: boolean | Ref<boolean>;
}

/**
 * useMilestones Hook 返回值
 */
export interface UseMilestonesReturn {
  /** 里程碑列表 */
  milestones: ComputedRef<Milestone[]>;
  /** 下一个里程碑 */
  nextMilestone: ComputedRef<Milestone | null>;
  /** 距离下一个里程碑的天数 */
  daysToNextMilestone: ComputedRef<number>;
  /** 当前连续天数（用于展示） */
  displayStreak: ComputedRef<number>;
  /** 判断里程碑是否已达成 */
  isMilestoneReached: (days: number) => boolean;
  /** 获取里程碑奖励金额 */
  getMilestoneReward: (days: number) => number;
}

/**
 * 解包 Ref 或普通值
 */
function unref<T>(value: T | Ref<T>): T {
  return (value as Ref<T>)?.value !== undefined
    ? (value as Ref<T>).value
    : (value as T);
}

/**
 * 从任务配置中提取里程碑天数列表
 */
export function extractMilestoneDays(tasks: TaskSnapshot[]): number[] {
  for (const task of tasks) {
    if (task.milestoneConfig?.milestones) {
      const milestones = task.milestoneConfig.milestones as Array<{
        value: number;
        label: string;
      }>;
      return milestones.map((m) => m.value);
    }
  }
  return [];
}

/**
 * 里程碑逻辑 Hook
 */
export function useMilestones(
  options: UseMilestonesOptions,
): UseMilestonesReturn {
  const {
    tasks,
    milestoneRewards,
    dailyCheckinStats,
    isEnglish = false,
  } = options;

  /**
   * 获取里程碑奖励金额
   */
  function getMilestoneReward(milestoneValue: number): number {
    const rewards = unref(milestoneRewards);
    return getMilestoneRewardValue(rewards, milestoneValue);
  }

  /**
   * 获取里程碑奖励名称（多语言）
   */
  function getMilestoneRewardName(milestoneValue: number): string | undefined {
    const rewards = unref(milestoneRewards);
    const isEn = unref(isEnglish);
    const reward = rewards.find((r) => r.days === milestoneValue);
    if (!reward?.rewardName) return undefined;
    return isEn ? reward.rewardName.en : reward.rewardName.zh;
  }

  /**
   * 判断里程碑是否已达成
   */
  function isMilestoneReached(milestoneDays: number): boolean {
    const stats = unref(dailyCheckinStats);
    return stats.maxStreak >= milestoneDays;
  }

  /**
   * 里程碑列表
   */
  const milestones = computed<Milestone[]>(() => {
    const taskList = unref(tasks);
    const milestoneDays = extractMilestoneDays(taskList);

    return milestoneDays.map((days) => ({
      days,
      reward: getMilestoneReward(days),
      rewardName: getMilestoneRewardName(days),
      claimed: isMilestoneReached(days),
    }));
  });

  /**
   * 当前连续天数（用于展示）
   */
  const displayStreak = computed(() => {
    const stats = unref(dailyCheckinStats);
    return stats.currentStreak;
  });

  /**
   * 下一个里程碑
   */
  const nextMilestone = computed<Milestone | null>(() => {
    const currentStreak = displayStreak.value;
    const milestoneList = milestones.value;

    if (milestoneList.length === 0) return null;

    // 找到第一个还未达成的里程碑
    const next = milestoneList.find((m) => m.days > currentStreak);

    // 如果所有里程碑都已达成，返回最后一个
    return next || milestoneList[milestoneList.length - 1];
  });

  /**
   * 距离下一个里程碑的天数
   */
  const daysToNextMilestone = computed(() => {
    if (!nextMilestone.value) return 0;
    const currentStreak = displayStreak.value;
    const remaining = nextMilestone.value.days - currentStreak;
    return remaining > 0 ? remaining : 0;
  });

  return {
    milestones,
    nextMilestone,
    daysToNextMilestone,
    displayStreak,
    isMilestoneReached,
    getMilestoneReward,
  };
}

export default useMilestones;
